import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../Modal";
import { paragraphsToRawHtml, rawHtmlToParagraphs } from "./paragraphHtml";
import { ParagraphRichEditor } from "./ParagraphRichEditor";

const PARAGRAPH_MODAL_CLASS = "flex min-h-[75vh] max-h-[85vh] w-full max-w-3xl flex-col";

type ParagraphBulkEditorModalProps = {
  show: boolean;
  paragraphs: string[];
  onSave: (paragraphs: string[]) => void;
  onCancel: () => void;
  title?: string;
};

/** Bulk paragraph editor modal with rich text editing and blank-line separation. */
export const ParagraphBulkEditorModal: React.FC<ParagraphBulkEditorModalProps> = ({
  show,
  paragraphs,
  onSave,
  onCancel,
  title = "Texte brut",
}) => {
  const [html, setHtml] = useState("");

  useEffect(() => {
    if (show) setHtml(paragraphsToRawHtml(paragraphs));
  }, [show, paragraphs]);

  const handleSave = useCallback(() => {
    const next = rawHtmlToParagraphs(html);
    onSave(next.length > 0 ? next : [""]);
    onCancel();
  }, [html, onSave, onCancel]);

  if (!show) return null;

  return (
    <Modal show={show} onHide={onCancel} size="lg" centered dialogClassName={PARAGRAPH_MODAL_CLASS}>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="flex flex-1 flex-col min-h-0">
        <p className="mb-3 text-xs text-slate-500">
          Separate paragraphs with a blank line (double line break). Use the toolbar for <strong style={{ color: "#005CA9" }}>bold</strong> and underline.
        </p>
        <ParagraphRichEditor
          value={html}
          onChange={setHtml}
          minHeight="calc(75vh - 12rem)"
          placeholder="First paragraph…&#10;&#10;Second paragraph (after a blank line)…"
          className="flex-1 min-h-0 flex flex-col"
        />
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-600 hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="rounded-lg bg-[#005CA9] px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
        >
          Save all
        </button>
      </Modal.Footer>
    </Modal>
  );
};
